import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  Radio,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import { Base_URL, ApiCall } from "../Common/Path";
import customFailAlert from "../Alerts/customFailAlert";
import customSuccessAlert from "../Alerts/customSuccessAlert";

/**
 * Add new members to an existing household.
 * Reuses the same 3-step flow as create-household (emails → accounts → permissions),
 * then calls POST /households/{id}/members to add each new member.
 */
const AddHouseholdMemberModal = ({ open, onClose, household, onMembersAdded }) => {
  const [activeStep, setActiveStep] = useState(0);

  // Email / account search
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [searchedAccounts, setSearchedAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [isSearchingAccounts, setIsSearchingAccounts] = useState(false);
  const [activeAccountTab, setActiveAccountTab] = useState(0);
  const [inactiveEmails, setInactiveEmails] = useState([]);
  const [selectedAccountTypes, setSelectedAccountTypes] = useState({});
  const [invitedEmails, setInvitedEmails] = useState([]);
  const [invitingEmails, setInvitingEmails] = useState(new Set()); // Track emails being invited

  // Permission selection per account (same model as CreateHouseholdModal)
  const [accountPermissions, setAccountPermissions] = useState({});
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [currentAccountForPermission, setCurrentAccountForPermission] =
    useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [isLoadingPermissionGroups, setIsLoadingPermissionGroups] =
    useState(false);
  const [permissionGroupsError, setPermissionGroupsError] = useState(null);
  const [investorPermissionGroups, setInvestorPermissionGroups] = useState({
    viewOnly: null,
    viewTrade: null,
  });
  // const [customPermissions, setCustomPermissions] = useState({
  //   createUser: false,
  //   updateUserInfo: false,
  //   viewReports: false,
  //   manageTransactions: false,
  //   editSettings: false,
  //   deleteUser: false,
  // });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = [
    "Enter Investor Emails",
    "Select Accounts",
    "Set Member Permissions",
  ];

  const resetState = () => {
    setActiveStep(0);
    setEmailInput("");
    setEmails([]);
    setSearchedAccounts([]);
    setSelectedAccounts([]);
    setIsSearchingAccounts(false);
    setActiveAccountTab(0);
    setInactiveEmails([]);
    setSelectedAccountTypes({});
    setInvitedEmails([]);
    setInvitingEmails(new Set());
    setAccountPermissions({});
    setPermissionModalOpen(false);
    setCurrentAccountForPermission(null);
    setSelectedTemplate("");
    setIsLoadingPermissionGroups(false);
    setPermissionGroupsError(null);
    setInvestorPermissionGroups({ viewOnly: null, viewTrade: null });
    // setCustomPermissions({
    //   createUser: false,
    //   updateUserInfo: false,
    //   viewReports: false,
    //   manageTransactions: false,
    //   editSettings: false,
    //   deleteUser: false,
    // });
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, household?.id]);

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value);
  };

  const addEmail = () => {
    const trimmed = emailInput.trim().replace(/,$/g, "");
    if (trimmed && isValidEmail(trimmed) && !emails.includes(trimmed)) {
      setEmails((prev) => [...prev, trimmed]);
    }
    setEmailInput("");
  };

  const handleEmailInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addEmail();
    }
  };

  const removeEmail = (emailToRemove) => {
    setEmails((prev) => prev.filter((e) => e !== emailToRemove));
  };

  // Normalize API user role object into account used by UI
  const mapUserRoleToAccount = (userRole, index) => {
    if (!userRole) return null;

    const user = userRole.user || {};
    const details = user.userDetailsDto || user.userInfo || {};

    const firstName = details.firstName || user.firstName || "";
    const lastName = details.lastName || user.lastName || "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ");

    const displayName =
      userRole.username ||
      userRole.name ||
      fullName ||
      user.email ||
      "Unknown Investor";

    const email = user.email || userRole.email || "";

    const rawType =
      userRole.investorType ||
      userRole.accountType ||
      userRole.type ||
      userRole.entity ||
      "";

    let typeLabel = "Individual";
    const normalizedType = String(rawType || "").toUpperCase();
    if (normalizedType.includes("CORP")) typeLabel = "Corporate";
    else if (normalizedType.includes("TRUST")) typeLabel = "Trust";

    return {
      id: userRole.id || userRole.userRoleId || index + 1,
      name: displayName,
      email,
      type: typeLabel,
      active: true,
      _raw: userRole,
    };
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "Individual":
        return "#10B981";
      case "Corporate":
        return "#8B5CF6";
      case "Trust":
        return "#F59E0B";
      default:
        return "#6B7280";
    }
  };

  const handleSearchAccounts = async () => {
    if (emails.length === 0 || isSearchingAccounts) return;
    setIsSearchingAccounts(true);
    setSearchedAccounts([]);
    setSelectedAccounts([]);
    setInactiveEmails([]);
    setInvitedEmails([]);
    setSelectedAccountTypes({});
    setActiveAccountTab(0);
    setAccountPermissions({});

    try {
      const url = `${Base_URL}/households/user-roles/by-emails`;
      const body = { emails };
      const response = await ApiCall(url, body, "post");

      const {
        statusCode,
        status,
        code,
        message,
        data: responseData,
      } = response || {};

      const isSuccess =
        statusCode === 200 ||
        statusCode === 201 ||
        code === 200 ||
        code === 201 ||
        String(status || "").toUpperCase() === "SUCCESS";

      if (!isSuccess) {
        customFailAlert(
          message || "Unable to fetch accounts for the provided emails."
        );
        return;
      }

      const data = responseData || response || {};
      let userRoles = [];

      if (Array.isArray(data)) userRoles = data;
      else if (Array.isArray(data.userRoles)) userRoles = data.userRoles;
      else if (Array.isArray(data.accounts)) userRoles = data.accounts;
      else if (Array.isArray(data.items)) userRoles = data.items;

      const mappedAccounts = userRoles
        .map((item, index) => mapUserRoleToAccount(item, index))
        .filter(Boolean)
        .filter((account) => {
          // Filter to only show INVESTOR roles, exclude SUB_INVESTOR roles
          const rawRole = account._raw;
          if (!rawRole) return true;
          
          // Check various possible role field names (including nested in user object)
          const roleType = 
            rawRole.role || 
            rawRole.roleType || 
            rawRole.groupRole || 
            rawRole.userType ||
            rawRole.user?.role ||
            rawRole.user?.roleType ||
            rawRole.user?.userType ||
            '';
          
          const normalizedRole = String(roleType).toUpperCase();
          
          // If role cannot be determined, include it (household user roles are typically INVESTOR)
          if (!normalizedRole) return true;
          
          // Exclude SUB_INVESTOR roles, only include INVESTOR roles
          return normalizedRole === 'INVESTOR';
        });

      setSearchedAccounts(mappedAccounts);

      // Prefer inactive email list from API if present
      let inactiveFromApi = [];
      if (Array.isArray(data.inactiveEmails)) {
        inactiveFromApi = data.inactiveEmails;
      } else if (Array.isArray(data.emailsNotFound)) {
        inactiveFromApi = data.emailsNotFound;
      }

      // Fallback: compute inactive emails from entered emails vs accounts found
      const emailsFound = new Set(
        mappedAccounts
          .map(acc => (acc.email || '').toLowerCase())
          .filter(Boolean)
      );

      const computedInactive = emails.filter(
        email => !emailsFound.has(email.toLowerCase())
      );

      const mergedInactive = Array.from(
        new Set([...(inactiveFromApi || []), ...computedInactive])
      );

      setInactiveEmails(mergedInactive);
      setActiveStep(1);
    } catch (error) {
      console.error("Error searching accounts for add-member:", error);
      customFailAlert("Something went wrong while searching accounts.");
    } finally {
      setIsSearchingAccounts(false);
    }
  };

  // Map UI account type to API investorType
  const mapAccountTypeToInvestorType = (accountType) => {
    switch (accountType) {
      case 'Individual':
        return 'INDIVIDUAL';
      case 'Corporate':
        return 'CORPORATE';
      case 'Trust':
        return 'TRUST';
      default:
        return 'INDIVIDUAL';
    }
  };

  // Handle invite inactive email
  const handleInviteEmail = async (email) => {
    const accountType = selectedAccountTypes[email];
    if (!accountType || invitingEmails.has(email)) return;

    // Set loading state for this email
    setInvitingEmails(prev => new Set([...prev, email]));

    try {
      // Map account type to API format
      const investorType = mapAccountTypeToInvestorType(accountType);

      // Prepare the request body according to API documentation
      const requestBody = {
        email: email,
        role: 'INVESTOR',
        investorType: investorType,
        entityInfo: {
          name: email.split('@')[0], // Use email prefix as default name
          entityType: 'INVESTOR'
        },
        isHouseholdMember: false
      };

      // Make API call to create user
      const url = `${Base_URL}/users`;
      const response = await ApiCall(url, requestBody, 'post');

      // Check if the response is successful
      const statusCode = response?.statusCode || response?.code;
      const isSuccess = statusCode === 200 || statusCode === 201;

      if (isSuccess) {
        // Extract userRoleId from response.data.userRole.id
        const userRoleId = response?.data?.userRole?.id;
        const userRoleData = response?.data?.userRole || {};
        
        if (!userRoleId) {
          customFailAlert('Failed to get user role ID from response.');
          return;
        }

        // Create account object similar to active accounts
        // Store the userRole object in _raw so handleSubmitMembers can access raw.id
        const invitedAccount = {
          id: userRoleId,
          name: email.split('@')[0], // Use email prefix as name
          email: email,
          type: accountType,
          active: true,
          _raw: userRoleData, // This contains the userRole object with the id field
          isInvited: true // Flag to identify invited users
        };

        // Add to invited emails with success status and account data
        setInvitedEmails(prev => [
          ...prev,
          {
            email,
            accountType,
            status: 'success',
            account: invitedAccount,
            userRoleId: userRoleId,
            response: userRoleData
          }
        ]);

        // Automatically add to selectedAccounts so it can be used in household creation
        setSelectedAccounts(prev => {
          // Check if already exists
          const exists = prev.find(acc => acc.email === email);
          if (exists) return prev;
          return [...prev, invitedAccount];
        });

        // Show success message
        customSuccessAlert('User invited successfully!');

        // Clear the selected account type for this email
        const newTypes = { ...selectedAccountTypes };
        delete newTypes[email];
        setSelectedAccountTypes(newTypes);
      } else {
        // Handle error response
        const errorMessage = response?.message || 'Failed to invite user. Please try again.';
        customFailAlert(errorMessage);
      }
    } catch (error) {
      console.error('Error inviting user:', error);
      customFailAlert('Something went wrong while inviting user. Please try again.');
    } finally {
      // Remove loading state for this email
      setInvitingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(email);
        return newSet;
      });
    }
  };

  // Handle account type change for inactive email
  const handleAccountTypeChange = (email, type) => {
    setSelectedAccountTypes({
      ...selectedAccountTypes,
      [email]: type
    });
  };

  const handleAddAccount = (account) => {
    setSelectedAccounts((prev) => {
      if (prev.find((a) => a.id === account.id)) return prev;
      return [...prev, account];
    });
  };

  const handleRemoveAccount = (accountId) => {
    setSelectedAccounts((prev) => prev.filter((a) => a.id !== accountId));
    setAccountPermissions((prev) => {
      const copy = { ...prev };
      delete copy[accountId];
      return copy;
    });
  };

  const handleOpenPermissionModal = (account) => {
    setCurrentAccountForPermission(account);
    setSelectedTemplate("");
    setPermissionModalOpen(true);
  };

  const handleClosePermissionModal = () => {
    setPermissionModalOpen(false);
    setCurrentAccountForPermission(null);
    setSelectedTemplate("");
    // setCustomPermissions({
    //   createUser: false,
    //   updateUserInfo: false,
    //   viewReports: false,
    //   manageTransactions: false,
    //   editSettings: false,
    //   deleteUser: false,
    // });
  };

  const normalizeGroupName = (name) =>
    String(name || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  // Fetch investor permission groups when permission modal opens
  useEffect(() => {
    const fetchInvestorPermissionGroups = async () => {
      if (!permissionModalOpen) return;
      if (investorPermissionGroups.viewOnly || investorPermissionGroups.viewTrade)
        return;

      setIsLoadingPermissionGroups(true);
      setPermissionGroupsError(null);

      try {
        const url = `${Base_URL}/permissions/groups?groupRole=INVESTOR`;
        const response = await ApiCall(url, null, "get");
        const groups = Array.isArray(response?.data) ? response.data : [];

        const viewOnlyGroup = groups.find((group) => {
          const normalized = normalizeGroupName(group.groupName);
          return normalized === "viewonly" || normalized === "readonly";
        });

        const viewTradeGroup = groups.find((group) => {
          const normalized = normalizeGroupName(group.groupName);
          return (
            normalized === "viewtrade" ||
            normalized === "viewandtrade" ||
            normalized === "tradeaccess"
          );
        });

        setInvestorPermissionGroups({
          viewOnly: viewOnlyGroup || null,
          viewTrade: viewTradeGroup || null,
        });
      } catch (error) {
        console.error("Error fetching investor permission groups:", error);
        setPermissionGroupsError("Unable to load permission templates.");
        setInvestorPermissionGroups({
          viewOnly: null,
          viewTrade: null,
        });
      } finally {
        setIsLoadingPermissionGroups(false);
      }
    };

    fetchInvestorPermissionGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionModalOpen]);

  const handleTemplateChange = (template) => {
    if (template === "view-only" && !investorPermissionGroups.viewOnly) return;
    if (template === "view-trade" && !investorPermissionGroups.viewTrade) return;

    setSelectedTemplate(template);

    // if (template === "custom") {
    //   setCustomPermissions({
    //     createUser: false,
    //     updateUserInfo: false,
    //     viewReports: false,
    //     manageTransactions: false,
    //     editSettings: false,
    //     deleteUser: false,
    //   });
    // }
  };

  // const handleCustomPermissionToggle = (permission) => {
  //   setCustomPermissions((prev) => ({
  //     ...prev,
  //     [permission]: !prev[permission],
  //   }));
  // };

  const handleContinuePermission = () => {
    if (!currentAccountForPermission || !selectedTemplate) return;

    const permissionLabel =
      selectedTemplate === "view-only"
        ? "View Only"
        : selectedTemplate === "view-trade"
        ? "View & Trade"
        : "Custom";

    let permissionsPayload;

    if (selectedTemplate === "view-only") {
      permissionsPayload = {
        type: "group",
        group: investorPermissionGroups.viewOnly || null,
      };
    } else if (selectedTemplate === "view-trade") {
      permissionsPayload = {
        type: "group",
        group: investorPermissionGroups.viewTrade || null,
      };
    } 
    // else {
    //   permissionsPayload = {
    //     type: "custom",
    //     values: { ...customPermissions },
    //   };
    // }

    setAccountPermissions((prev) => ({
      ...prev,
      [currentAccountForPermission.id]: {
        template: selectedTemplate,
        label: permissionLabel,
        permissions: permissionsPayload,
      },
    }));

    handleClosePermissionModal();
  };

  const canProceedToAccounts = emails.length > 0 && !isSearchingAccounts;
  const canProceedToPermissions = selectedAccounts.length > 0;
  const canAddMembers =
    selectedAccounts.length > 0 &&
    selectedAccounts.every((acc) => !!accountPermissions[acc.id]);

  const handleNext = () => {
    if (activeStep === 0 && canProceedToAccounts) {
      handleSearchAccounts();
    } else if (activeStep === 1 && canProceedToPermissions) {
      setActiveStep(2);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) return;
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmitMembers = async () => {
    if (!household || !canAddMembers || isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Prepare new members to add
      const membersToAdd = selectedAccounts.map((acc) => {
        const raw = acc._raw || {};
        // Extract userRoleId - for invited users, it's raw.id (userRole.id)
        // For active accounts, it could be raw.id or raw.userRoleId
        const userRoleId = raw.id || raw.userRoleId || acc.id;
        const meta = accountPermissions[acc.id];
        const perms = meta?.permissions || {};
        const group = perms.type === "group" ? perms.group : null;

        return {
          userRoleId,
          permissionGroupId: group?.id || null,
        };
      });

      // Add each member using POST /households/{id}/members (parallel requests)
      const url = `${Base_URL}/households/${household.id}/members`;
      
      const addMemberPromises = membersToAdd.map(async (member) => {
        try {
          const body = {
            userRoleId: member.userRoleId,
            permissionGroupId: member.permissionGroupId || null,
            invitationExpiryDays: 7,
          };

          const response = await ApiCall(url, body, "post");
          const statusCode = response?.statusCode || response?.code;

          const ok =
            statusCode === 200 ||
            statusCode === 201 ||
            String(response?.status || "").toUpperCase() === "SUCCESS";

          if (ok) {
            return { success: true, response, member };
          } else {
            return {
              success: false,
              error: response?.message || `Failed to add member: ${member.userRoleId}`,
              member,
            };
          }
        } catch (error) {
          console.error(`Error adding member ${member.userRoleId}:`, error);
          return {
            success: false,
            error: `Failed to add member: ${member.userRoleId}`,
            member,
          };
        }
      });

      // Wait for all requests to complete
      const results = await Promise.allSettled(addMemberPromises);
      
      const successful = results
        .filter((r) => r.status === "fulfilled" && r.value.success)
        .map((r) => r.value);
      const failed = results
        .filter(
          (r) => r.status === "rejected" || (r.status === "fulfilled" && !r.value.success)
        )
        .map((r) =>
          r.status === "rejected"
            ? r.reason?.message || "Unknown error"
            : r.value.error
        );

      // Show appropriate feedback
      if (failed.length > 0 && successful.length === 0) {
        // All failed
        const errorMessage = failed[0] || "Unable to add household members. Please try again.";
        customFailAlert(errorMessage);
        if (onMembersAdded) {
          onMembersAdded({ success: false, message: errorMessage });
        }
        return;
      } else if (failed.length > 0) {
        // Some succeeded, some failed
        const message = `Successfully added ${successful.length} member(s), but ${failed.length} failed.`;
        customFailAlert(message);
        if (onMembersAdded) {
          onMembersAdded({ success: true, message, partial: true });
        }
      } else {
        // All succeeded
        if (onMembersAdded) {
          onMembersAdded(successful[0]?.response || { success: true, message: "Member(s) added successfully" });
        }
      }

      // Only close if at least one member was added successfully
      if (successful.length > 0) {
        resetState();
        onClose();
      }
    } catch (error) {
      console.error("Error adding household members:", error);
      const errorMessage = "Something went wrong while adding members.";
      customFailAlert(errorMessage);
      if (onMembersAdded) {
        onMembersAdded({ success: false, message: errorMessage });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          width: "720px",
        },
      }}
    >
      <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#000", mb: 0.5 }}
            >
              Add Member
            </Typography>
            <Typography variant="body2" sx={{ color: "#6B7280" }}>
              Following accounts will be linked together in a household
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: "#6B7280", mt: -1 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 1, pb: 3, ml: 6 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {steps.map((label, index) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border:
                      index === activeStep
                        ? "3px solid #4F46E5"
                        : "2px solid #D1D5DB",
                    backgroundColor:
                      index < activeStep ? "#4F46E5" : "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    zIndex: 2,
                  }}
                >
                  {index < activeStep ? (
                    <CheckIcon sx={{ color: "#fff", fontSize: 20 }} />
                  ) : (
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor:
                          index === activeStep ? "#4F46E5" : "#D1D5DB",
                      }}
                    />
                  )}
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      flex: 1,
                      height: 2,
                      backgroundColor:
                        index < activeStep ? "#4F46E5" : "#E5E7EB",
                      ml: -1,
                      mr: -1,
                      zIndex: 1,
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  ml: -6,
                  color:
                    index < activeStep || index === activeStep
                      ? "#111827"
                      : "#9CA3AF",
                  fontWeight: index === activeStep ? 600 : 500,
                  fontSize: "11px",
                  textAlign: "center",
                  lineHeight: 1.2,
                }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, pb: 3, pt: 0, minHeight: "280px" }}>
        {/* Step 0: Enter Investor Emails */}
        {activeStep === 0 && (
          <Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Enter one or more investors' emails to search accounts"
              value={emailInput}
              onChange={handleEmailInputChange}
              onKeyDown={handleEmailInputKeyDown}
              onBlur={addEmail}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                  padding: 1.5,
                  "& fieldset": {
                    borderColor: "#2563EB",
                  },
                  "&:hover fieldset": {
                    borderColor: "#2563EB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2563EB",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: emails.length > 0 ? 1 : 0,
                    }}
                  >
                    {emails.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => removeEmail(email)}
                        sx={{
                          backgroundColor: "#F3F4F6",
                          borderRadius: "4px",
                          "& .MuiChip-deleteIcon": {
                            color: "#6B7280",
                            "&:hover": {
                              color: "#374151",
                            },
                          },
                        }}
                      />
                    ))}
                  </Box>
                ),
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: "#6B7280", mt: 1, display: "block" }}
            >
              Press Enter, comma, or space to add multiple emails
            </Typography>
          </Box>
        )}

        {/* Step 1: Select Accounts */}
        {activeStep === 1 && (
          <Box>
            {/* Tabs */}
            <Tabs
              value={activeAccountTab}
              onChange={(e, newValue) => setActiveAccountTab(newValue)}
              sx={{
                mb: 3,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#6B7280',
                  '&.Mui-selected': {
                    color: '#2563EB',
                    fontWeight: 600
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#2563EB'
                }
              }}
            >
              <Tab label="Active Accounts" />
              <Tab label="In-active emails" />
            </Tabs>

            {/* Account List */}
            {activeAccountTab === 0 && (
              <Box sx={{ maxHeight: "420px", overflowY: "auto" }}>
                {searchedAccounts.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography sx={{ color: "#6B7280", fontSize: "14px" }}>
                      No accounts found. Try different emails.
                    </Typography>
                  </Box>
                ) : (
                  searchedAccounts.map((account) => {
                    const isAdded = selectedAccounts.some(
                      (a) => a.id === account.id
                    );
                    return (
                      <Box
                        key={account.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 2,
                          borderBottom: "1px solid #E5E7EB",
                          "&:last-child": { borderBottom: "none" },
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#000",
                              }}
                            >
                              {account.name}
                            </Typography>
                            <Chip
                              label={account.type}
                              size="small"
                              sx={{
                                backgroundColor: `${getTypeColor(
                                  account.type
                                )}20`,
                                color: getTypeColor(account.type),
                                fontWeight: 500,
                                fontSize: "11px",
                                height: "20px",
                              }}
                            />
                          </Box>
                          <Typography
                            sx={{ fontSize: "13px", color: "#6B7280" }}
                          >
                            {account.email}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            isAdded
                              ? handleRemoveAccount(account.id)
                              : handleAddAccount(account)
                          }
                          sx={{
                            textTransform: "none",
                            borderColor: "#2563EB",
                            color: "#2563EB",
                            px: 3,
                            py: 0.75,
                            borderRadius: "4px",
                            fontWeight: 600,
                            "&:hover": {
                              backgroundColor: "#EFF6FF",
                              borderColor: "#2563EB",
                            },
                          }}
                        >
                          {isAdded ? "Remove" : "Add"}
                        </Button>
                      </Box>
                    );
                  })
                )}
              </Box>
            )}

            {activeAccountTab === 1 && (
              <Box sx={{ maxHeight: "420px", overflowY: "auto" }}>
                {(() => {
                  const remainingInactiveEmails = inactiveEmails.filter(
                    email => !invitedEmails.find(inv => inv.email === email && inv.status === 'success')
                  );
                  return remainingInactiveEmails.length === 0 && invitedEmails.length === 0;
                })() ? (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Typography sx={{ color: "#6B7280", fontSize: "14px" }}>
                      No inactive emails found
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Invited emails with status */}
                    {invitedEmails.map((item, index) => (
                      <Box
                        key={`invited-${index}`}
                        sx={{
                          py: 2,
                          borderBottom: '1px solid #E5E7EB',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: item.status === 'pending' ? 2 : 0 }}>
                          <Box>
                            <Typography sx={{ fontSize: '14px', color: '#000', fontWeight: 500 }}>
                              {item.email}
                            </Typography>
                            {item.accountType && (
                              <Chip
                                label={item.accountType}
                                size="small"
                                sx={{
                                  backgroundColor: `${getTypeColor(item.accountType)}20`,
                                  color: getTypeColor(item.accountType),
                                  fontWeight: 500,
                                  fontSize: '11px',
                                  height: '20px',
                                  mt: 0.5
                                }}
                              />
                            )}
                          </Box>
                          <Chip
                            label={item.status === 'success' ? 'Invited' : 'Pending'}
                            size="small"
                            sx={{
                              backgroundColor: item.status === 'success' ? '#ECFDF3' : '#FEF3C7',
                              color: item.status === 'success' ? '#15803D' : '#D97706',
                              fontWeight: 500,
                              fontSize: '12px'
                            }}
                          />
                        </Box>
                        {item.status === 'pending' && (
                          <LinearProgress
                            sx={{
                              height: 6,
                              borderRadius: '4px',
                              backgroundColor: '#FEF3C7',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#F59E0B',
                                borderRadius: 2
                              }
                            }}
                          />
                        )}
                      </Box>
                    ))}
                    
                    {/* Inactive emails that haven't been invited yet */}
                    {inactiveEmails
                      .filter(email => !invitedEmails.find(inv => inv.email === email && inv.status === 'success'))
                      .map((email, index) => (
                        <Box
                          key={`inactive-${index}`}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 2,
                            borderBottom: '1px solid #E5E7EB',
                            gap: 2
                          }}
                        >
                          <Typography sx={{ fontSize: '14px', color: '#000', minWidth: '200px' }}>
                            {email}
                          </Typography>
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select
                              value={selectedAccountTypes[email] || ''}
                              onChange={(e) => handleAccountTypeChange(email, e.target.value)}
                              displayEmpty
                              sx={{
                                borderRadius: '4px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#D1D5DB'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#9CA3AF'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#2563EB'
                                }
                              }}
                            >
                              <MenuItem value="" disabled>
                                Account type
                              </MenuItem>
                              <MenuItem value="Individual">Individual</MenuItem>
                              <MenuItem value="Corporate">Corporate</MenuItem>
                              <MenuItem value="Trust">Trust</MenuItem>
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            disabled={!selectedAccountTypes[email] || invitingEmails.has(email)}
                            onClick={() => handleInviteEmail(email)}
                            sx={{
                              textTransform: 'none',
                              borderColor: selectedAccountTypes[email] && !invitingEmails.has(email) ? '#2563EB' : '#D1D5DB',
                              color: selectedAccountTypes[email] && !invitingEmails.has(email) ? '#2563EB' : '#9CA3AF',
                              px: 3,
                              py: 0.75,
                              borderRadius: '4px',
                              fontWeight: 600,
                              minWidth: '100px',
                              '&:hover': {
                                backgroundColor: selectedAccountTypes[email] && !invitingEmails.has(email) ? '#EFF6FF' : 'transparent',
                                borderColor: selectedAccountTypes[email] && !invitingEmails.has(email) ? '#2563EB' : '#D1D5DB'
                              },
                              '&.Mui-disabled': {
                                borderColor: '#D1D5DB',
                                color: '#9CA3AF'
                              }
                            }}
                          >
                            {invitingEmails.has(email) ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={14} sx={{ color: '#9CA3AF' }} />
                                <span>Inviting...</span>
                              </Box>
                            ) : (
                              'Invite'
                            )}
                          </Button>
                        </Box>
                      ))}
                  </>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Step 2: Set Member Permissions */}
        {activeStep === 2 && (
          <Box sx={{ maxHeight: "450px", overflowY: "auto" }}>
            {selectedAccounts.map((account) => (
              <Box
                key={account.id}
                sx={{
                  py: 2,
                  px: 2,
                  borderBottom: "1px solid #E5E7EB",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#000",
                        }}
                      >
                        {account.name}
                      </Typography>
                      <Chip
                        label={account.type}
                        size="small"
                        sx={{
                          backgroundColor: `${getTypeColor(
                            account.type
                          )}20`,
                          color: getTypeColor(account.type),
                          fontWeight: 500,
                          fontSize: "11px",
                          height: "20px",
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{ fontSize: "13px", color: "#6B7280" }}
                    >
                      {account.email}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      ml: 2,
                    }}
                  >
                    {accountPermissions[account.id] ? (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => handleOpenPermissionModal(account)}
                          sx={{
                            textTransform: "none",
                            borderColor: "#2563EB",
                            color: "#2563EB",
                            px: 3,
                            py: 0.75,
                            borderRadius: "4px",
                            fontWeight: 600,
                            minWidth: "180px",
                            "&:hover": {
                              backgroundColor: "#EFF6FF",
                              borderColor: "#2563EB",
                            },
                          }}
                        >
                          {accountPermissions[account.id].label}
                        </Button>
                        <IconButton
                          onClick={() => handleOpenPermissionModal(account)}
                          sx={{
                            color: "#2563EB",
                            "&:hover": {
                              backgroundColor: "#EFF6FF",
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <Button
                        variant="outlined"
                        onClick={() => handleOpenPermissionModal(account)}
                        sx={{
                          textTransform: "none",
                          borderColor: "#2563EB",
                          color: "#2563EB",
                          backgroundColor: "#fff",
                          px: 3,
                          py: 0.75,
                          borderRadius: "4px",
                          fontWeight: 500,
                          minWidth: "180px",
                          "&:hover": {
                            borderColor: "#1D4ED8",
                            backgroundColor: "#EFF6FF",
                          },
                        }}
                      >
                        Select Permission Type
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 2 }}>
        <Button
          onClick={handleBack}
          variant="outlined"
          disabled={activeStep === 0}
          sx={{
            textTransform: "none",
            px: 4,
            py: 1.1,
            borderRadius: "4px",
            borderColor: activeStep === 0 ? "#E5E7EB" : "#D1D5DB",
            color: activeStep === 0 ? "#9CA3AF" : "#374151",
            fontWeight: 600,
            "&:hover": {
              borderColor: activeStep === 0 ? "#E5E7EB" : "#9CA3AF",
              backgroundColor: activeStep === 0 ? "#FFFFFF" : "#F9FAFB",
            },
          }}
        >
          Back
        </Button>
        <Box sx={{ flex: 1 }} />

        {activeStep < 2 && (
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={
              (activeStep === 0 && !canProceedToAccounts) ||
              (activeStep === 1 && !canProceedToPermissions)
            }
            sx={{
              textTransform: "none",
              px: 4,
              py: 1.1,
              borderRadius: "4px",
              backgroundColor:
                (activeStep === 0 && canProceedToAccounts) ||
                (activeStep === 1 && canProceedToPermissions)
                  ? "#2563EB"
                  : "#E5E7EB",
              color:
                (activeStep === 0 && canProceedToAccounts) ||
                (activeStep === 1 && canProceedToPermissions)
                  ? "#FFFFFF"
                  : "#9CA3AF",
              fontWeight: 600,
              "&:hover": {
                backgroundColor:
                  (activeStep === 0 && canProceedToAccounts) ||
                  (activeStep === 1 && canProceedToPermissions)
                    ? "#1D4ED8"
                    : "#E5E7EB",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E5E7EB",
                color: "#9CA3AF",
              },
            }}
          >
            {activeStep === 0 ? "Search Account" : "Next"}
          </Button>
        )}

        {activeStep === 2 && (
          <Button
            onClick={handleSubmitMembers}
            variant="contained"
            disabled={!canAddMembers || isSubmitting}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1.1,
              borderRadius: "4px",
              backgroundColor:
                canAddMembers && !isSubmitting ? "#2563EB" : "#E5E7EB",
              color: canAddMembers && !isSubmitting ? "#FFFFFF" : "#9CA3AF",
              fontWeight: 600,
              "&:hover": {
                backgroundColor:
                  canAddMembers && !isSubmitting ? "#1D4ED8" : "#E5E7EB",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E5E7EB",
                color: "#9CA3AF",
              },
            }}
          >
            {isSubmitting ? "Adding..." : "Add Member(s)"}
          </Button>
        )}
      </DialogActions>

      {/* Permission Template Modal */}
      <Dialog
        open={permissionModalOpen}
        onClose={handleClosePermissionModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            width: "550px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#000", mb: 0.5 }}
              >
                Select Permission Template
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                Following accounts will be linked together in a household
              </Typography>
            </Box>
            <IconButton
              onClick={handleClosePermissionModal}
              sx={{ color: "#6B7280", mt: -1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 4, position: "relative" }}>
            {permissionGroupsError && (
              <Typography
                sx={{
                  position: "absolute",
                  top: -24,
                  left: 0,
                  fontSize: "12px",
                  color: "#DC2626",
                }}
              >
                {permissionGroupsError}
              </Typography>
            )}

            {/* View-Only */}
            <Box
              onClick={() => handleTemplateChange("view-only")}
              sx={{
                flex: 1,
                border:
                  selectedTemplate === "view-only"
                    ? "2px solid #2563EB"
                    : "2px solid #E5E7EB",
                borderRadius: 3,
                p: 2.5,
                cursor:
                  !isLoadingPermissionGroups &&
                  !investorPermissionGroups.viewOnly
                    ? "not-allowed"
                    : "pointer",
                position: "relative",
                transition: "all 0.2s",
                opacity:
                  !isLoadingPermissionGroups &&
                  !investorPermissionGroups.viewOnly
                    ? 0.5
                    : 1,
                pointerEvents:
                  !isLoadingPermissionGroups &&
                  !investorPermissionGroups.viewOnly
                    ? "none"
                    : "auto",
                "&:hover": {
                  borderColor: "#2563EB",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              <Radio
                checked={selectedTemplate === "view-only"}
                disabled={!investorPermissionGroups.viewOnly && !isLoadingPermissionGroups}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  padding: 0,
                  color: "#D1D5DB",
                  "&.Mui-checked": {
                    color: "#2563EB",
                  },
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#000",
                  textAlign: "center",
                  mt: 1,
                }}
              >
                View-Only
              </Typography>
            </Box>

            {/* View & Trade */}
            <Box
              onClick={() => handleTemplateChange("view-trade")}
              sx={{
                flex: 1,
                border:
                  selectedTemplate === "view-trade"
                    ? "2px solid #2563EB"
                    : "2px solid #E5E7EB",
                borderRadius: 3,
                p: 2.5,
                cursor:
                  !isLoadingPermissionGroups &&
                  !investorPermissionGroups.viewTrade
                    ? "not-allowed"
                    : "pointer",
                position: "relative",
                transition: "all 0.2s",
                opacity:
                  !isLoadingPermissionGroups &&
                  !investorPermissionGroups.viewTrade
                    ? 0.5
                    : 1,
                pointerEvents:
                  !isLoadingPermissionGroups &&
                  !investorPermissionGroups.viewTrade
                    ? "none"
                    : "auto",
                "&:hover": {
                  borderColor: "#2563EB",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              <Radio
                checked={selectedTemplate === "view-trade"}
                disabled={!investorPermissionGroups.viewTrade && !isLoadingPermissionGroups}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  padding: 0,
                  color: "#D1D5DB",
                  "&.Mui-checked": {
                    color: "#2563EB",
                  },
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#000",
                  textAlign: "center",
                  mt: 1,
                }}
              >
                View & Trade
              </Typography>
            </Box>

            {/* Custom */}
            {/* <Box
              onClick={() => handleTemplateChange("custom")}
              sx={{
                flex: 1,
                border:
                  selectedTemplate === "custom"
                    ? "2px solid #2563EB"
                    : "2px solid #E5E7EB",
                borderRadius: 3,
                p: 2.5,
                cursor: "pointer",
                position: "relative",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#2563EB",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              <Radio
                checked={selectedTemplate === "custom"}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  padding: 0,
                  color: "#D1D5DB",
                  "&.Mui-checked": {
                    color: "#2563EB",
                  },
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#000",
                  textAlign: "center",
                  mt: 1,
                }}
              >
                Custom
              </Typography>
            </Box> */}
          </Box>

          {/* Permissions for group templates */}
          {isLoadingPermissionGroups && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 18, color: "#2563EB" }} />
              <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                Loading permission templates...
              </Typography>
            </Box>
          )}

          {selectedTemplate === "view-only" &&
            investorPermissionGroups.viewOnly && (
              <Box
                sx={{
                  mt: 1,
                  borderRadius: 3,
                  border: "1px solid #E5E7EB",
                  px: 3,
                  py: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#111827",
                    mb: 1,
                  }}
                >
                  {investorPermissionGroups.viewOnly.groupName}
                </Typography>
                <List
                  dense
                  sx={{ maxHeight: 220, overflow: "auto" }}
                >
                  {(investorPermissionGroups.viewOnly.apiNames || []).map(
                    (api) => (
                      <ListItem key={api} sx={{ py: 0.25 }}>
                        <CheckCircleIcon
                          sx={{ color: "#16A34A", fontSize: 20, mr: 1.5 }}
                        />
                        <ListItemText
                          primary={api}
                          primaryTypographyProps={{
                            fontSize: "13px",
                            color: "#374151",
                          }}
                        />
                      </ListItem>
                    )
                  )}
                  {(!investorPermissionGroups.viewOnly.apiNames ||
                    investorPermissionGroups.viewOnly.apiNames.length ===
                      0) && (
                    <ListItem>
                      <ListItemText
                        primary="No APIs configured for this group."
                        primaryTypographyProps={{
                          fontSize: "13px",
                          color: "#6B7280",
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

          {selectedTemplate === "view-trade" &&
            investorPermissionGroups.viewTrade && (
              <Box
                sx={{
                  mt: 1,
                  borderRadius: 3,
                  border: "1px solid #E5E7EB",
                  px: 3,
                  py: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#111827",
                    mb: 1,
                  }}
                >
                  {investorPermissionGroups.viewTrade.groupName}
                </Typography>
                <List
                  dense
                  sx={{ maxHeight: 220, overflow: "auto" }}
                >
                  {(investorPermissionGroups.viewTrade.apiNames || []).map(
                    (api) => (
                      <ListItem key={api} sx={{ py: 0.25 }}>
                        <CheckCircleIcon
                          sx={{ color: "#16A34A", fontSize: 20, mr: 1.5 }}
                        />
                        <ListItemText
                          primary={api}
                          primaryTypographyProps={{
                            fontSize: "13px",
                            color: "#374151",
                          }}
                        />
                      </ListItem>
                    )
                  )}
                  {(!investorPermissionGroups.viewTrade.apiNames ||
                    investorPermissionGroups.viewTrade.apiNames.length ===
                      0) && (
                    <ListItem>
                      <ListItemText
                        primary="No APIs configured for this group."
                        primaryTypographyProps={{
                          fontSize: "13px",
                          color: "#6B7280",
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
            )}

          {/* {selectedTemplate === "custom" && (
            <Box>
              {[
                { key: "createUser", label: "Create User" },
                { key: "updateUserInfo", label: "Update User Info" },
                { key: "viewReports", label: "View Reports" },
                { key: "manageTransactions", label: "Manage Transactions" },
                { key: "editSettings", label: "Edit Settings" },
                { key: "deleteUser", label: "Delete User" },
              ].map((item) => (
                <FormControlLabel
                  key={item.key}
                  control={
                    <Checkbox
                      checked={customPermissions[item.key]}
                      onChange={() =>
                        handleCustomPermissionToggle(item.key)
                      }
                      sx={{
                        color: "#D1D5DB",
                        "&.Mui-checked": {
                          color: "#10B981",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{ fontSize: "14px", color: "#374151" }}
                    >
                      {item.label}
                    </Typography>
                  }
                  sx={{ mb: 1.5, width: "100%" }}
                />
              ))}
            </Box>
          )} */}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 3, gap: 2 }}>
          <Button
            onClick={handleClosePermissionModal}
            variant="outlined"
            sx={{
              textTransform: "none",
              px: 4,
              py: 1.1,
              borderRadius: "4px",
              borderColor: "#D1D5DB",
              color: "#374151",
              fontWeight: 600,
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleContinuePermission}
            variant="contained"
            disabled={!selectedTemplate}
            sx={{
              textTransform: "none",
              px: 4,
              py: 1.1,
              borderRadius: "4px",
              backgroundColor: selectedTemplate ? "#2563EB" : "#E5E7EB",
              color: selectedTemplate ? "#FFFFFF" : "#9CA3AF",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: selectedTemplate ? "#1D4ED8" : "#E5E7EB",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E5E7EB",
                color: "#9CA3AF",
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AddHouseholdMemberModal;


